import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { isNullOrEmptyString } from '@progress/kendo-data-query/dist/npm/utils'
import { EditorContext } from '@/components/report-tables/core/table'

const areEditorPropsEqual = (_, __) => {
  return true
}

const InputEditorFunc = React.memo(function InputEditorFunc ({
  style,
  getValue = (value, char) => {
    return { value: value + char }
  },
  validateValue = (_) => true,
  onInputChange = null,
  render = null
}) {
  const inputRef = useRef()
  const { onChange, onSelectionChange, editorValue, selectionStart, selectionEnd, reverseTransform, reverseTransformed } = useContext(EditorContext)
  const [error, setError] = useState(false)

  const showError = () => { setError(true) }

  // Transforms the persistence value to user input value on editor creation
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus()
    const value = getValue(editorValue, '').value
    if (validateValue(value)) {
      reverseTransform({ getValue, validateValue })
    } else {
      showError()
      onChange('')
    }
    // Value is loaded this way only on mounting
    // eslint-disable-next-line
  }, [])

  // Autofocus effect and set selection on load
  useEffect(() => {
    if (inputRef.current) { inputRef.current.setSelectionRange(selectionStart, selectionEnd) }
  }, [selectionStart, selectionEnd, reverseTransformed])

  useEffect(() => {
    // Correct the position of cursor after error
    let timeoutId
    if (error) {
      if (inputRef.current) inputRef.current.setSelectionRange(selectionStart, selectionEnd)
      timeoutId = setTimeout(() => {
        setError(false)
      }, 300)
    }
    return () => clearTimeout(timeoutId)
  }, [error, selectionEnd, selectionStart])

  const onInputChangeInternal = useCallback(event => {
    event.preventDefault()
    event.stopPropagation()
    preventSelectionCallback.current = true

    const value = event.target.value

    if (typeof onInputChange === 'function') {
      onInputChange(event, showError)
    } else if (isNullOrEmptyString(value) || validateValue(value)) {
      onChange({ value: value, start: event.target.selectionStart, end: event.target.selectionEnd })
    } else {
      setError(true)
    }
  }, [onChange, onInputChange, validateValue])

  const onSelectionChangeInternal = useCallback(event => {
    if (preventSelectionCallback.current) {
      preventSelectionCallback.current = false
    } else {
      onSelectionChange(event.target.selectionStart, event.target.selectionEnd)
    }
  }, [onSelectionChange])

  const preventSelectionCallback = useRef(false)
  const renderInternal = () => {
    if (typeof render === 'function') return render(editorValue)
    else {
      return <input
        ref={inputRef}
        onChange={onInputChangeInternal}
        onSelect={onSelectionChangeInternal}
        value={editorValue}
        style={style}
        className={error ? 'htCellHasError' : ''}
      />
    }
  }
  return renderInternal()
})

InputEditorFunc.propTypes = {
  value: PropTypes.any,
  style: PropTypes.string,
  // Function to get pure input value as well as starting selection and persistence function
  getValue: PropTypes.func,
  // Validation function. Returns boolean.
  validateValue: PropTypes.func,
  // Overrides the default input's onchange behavior
  onInputChange: PropTypes.func
}

export { InputEditorFunc, areEditorPropsEqual }



// WEBPACK FOOTER //
// ./src/components/report-tables/editors/fInputEditor.js